import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, LogOut, ChevronDown, PawPrint, Menu, Moon, Sun, Shield, Building2 } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';
import { Link } from 'react-router-dom';
import { getPets } from '../../services/petService';
import { getOwners } from '../../services/ownerService';
import { getExaminations } from '../../services/examinationService';
import { getVaccinations } from '../../services/vaccinationService';
import { Pet, Owner, Examination, Vaccination, SessionUser } from '../../types';
import PetDetailModal from '../Modals/PetDetailModal';

interface HeaderProps {
  user: SessionUser;
  onLogout?: () => void;
  onToggleSidebar?: () => void;
  sidebarOpen?: boolean;
  className?: string;
}

const DarkModeToggle: React.FC = () => {
  const { isDarkMode, toggleDarkMode } = useTheme();
  return (
    <button
      onClick={toggleDarkMode}
      title={isDarkMode ? 'Açık moda geç' : 'Karanlık moda geç'}
      className="w-8 h-8 rounded-xl border border-[#E8E2D4] hover:bg-[#F0EBE1] transition-colors flex items-center justify-center text-[#8A8676]"
    >
      {isDarkMode ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4" />}
    </button>
  );
};

const Header: React.FC<HeaderProps> = ({ user, onLogout, onToggleSidebar, className }) => {
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Array<Pet & { owner?: Owner; lastExamination?: Examination }>>([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [isPetDetailModalOpen, setIsPetDetailModalOpen] = useState(false);
  const [selectedPetForModal, setSelectedPetForModal] = useState< (Pet & { owner?: Owner; vaccinations?: Vaccination[]; examinations?: Examination[] }) | null>(null);

  const { data: pets = [] } = useQuery<Pet[]>({ queryKey: ['pets'], queryFn: getPets });
  const { data: owners = [] } = useQuery<Owner[]>({ queryKey: ['owners'], queryFn: getOwners });
  const { data: examinations = [] } = useQuery<Examination[]>({ queryKey: ['examinations'], queryFn: getExaminations });
  const { data: vaccinations = [] } = useQuery<Vaccination[]>({ queryKey: ['vaccinations'], queryFn: getVaccinations });
  const userName = user.name || user.email;
  const userRole = user.role;
  const clinicLine = user.clinicName?.trim();
  const userInitials = userName
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();

  const handleSearch = (value: string) => {
    setSearchTerm(value);
    if (value.trim().length > 1) {
      const lowerCaseValue = value.toLowerCase();
      const results = pets
        .filter(pet => 
          pet.name.toLowerCase().includes(lowerCaseValue) ||
          pet.breed?.toLowerCase().includes(lowerCaseValue) ||
          pet.protocolNo?.toLowerCase().includes(lowerCaseValue) ||
          owners.find(o => o.id === pet.owner_id)?.name.toLowerCase().includes(lowerCaseValue)
        )
        .map(pet => {
          const owner = owners.find(o => o.id === pet.owner_id);
          const petExaminations = examinations.filter(ex => ex.pet_id === pet.id || ex.petId === pet.id).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
          const petVaccinations = vaccinations.filter(v => v.pet_id === pet.id || v.petId === pet.id).sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
          
          return {
            ...pet,
            owner,
            examinations: petExaminations,
            vaccinations: petVaccinations,
            lastExamination: petExaminations[0]
          };
        })
        .slice(0, 5);
      setSearchResults(results);
      setShowSearchResults(true);
    } else {
      setSearchResults([]);
      setShowSearchResults(false);
    }
  };

  const handleLogout = () => {
    setShowUserMenu(false);
    if (onLogout) {
      onLogout();
    }
  };

  const handleResultClick = (pet: Pet & { owner?: Owner; vaccinations?: Vaccination[]; examinations?: Examination[] }) => {
    setSearchTerm('');
    setShowSearchResults(false);
    setSelectedPetForModal(pet);
    setIsPetDetailModalOpen(true);
  };

  return (
    <header
      className={`sticky top-0 z-[130] w-full shrink-0 h-16 ${className || ''}`}
      style={{ backgroundColor: 'var(--color-bg-page)', borderBottom: '1px solid var(--color-border)' }}
    >
      <div className="h-full flex items-center justify-between px-4 lg:px-6">
        <div className="flex min-w-0 flex-1 items-center gap-2 pr-2">
          {/* Mobil hamburger */}
          <button
            onClick={onToggleSidebar}
            className="lg:hidden flex-shrink-0 p-2 rounded-xl hover:bg-[#F0EBE1] transition-colors text-slate-600"
            aria-label="Menüyü aç"
          >
            <Menu className="w-5 h-5" />
          </button>
          {/* Arama Çubuğu */}
          <div className="relative z-[90] w-full max-w-[14rem] sm:max-w-[16rem] lg:max-w-xs xl:max-w-sm">
            <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
              <Search className="h-4 w-4 text-slate-400" />
            </div>
            <input
              type="text"
              placeholder="Hızlı ara..."
              value={searchTerm}
              onChange={(e) => handleSearch(e.target.value)}
              onFocus={() => searchTerm.length > 1 && setShowSearchResults(true)}
              className="w-full rounded-xl py-1.5 pl-9 pr-14 text-[13px] transition-colors outline-none"
              style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)', color: 'var(--color-text-strong)' }}
            />
            {/* Cmd+K ipucu */}
            <kbd
              className="absolute right-2 top-1/2 -translate-y-1/2 hidden sm:inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-bold text-[#8A8676] bg-white border border-[#E8E2D4] rounded pointer-events-none"
              title="Komut paletini aç"
            >
              ⌘K
            </kbd>
            {showSearchResults && searchResults.length > 0 && (
              <div className="absolute mt-2 w-full rounded-xl shadow-xl z-50 max-h-96 overflow-y-auto"
                style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
                {searchResults.map(pet => (
                  <button
                    key={pet.id}
                    onClick={() => handleResultClick(pet)}
                    className="w-full text-left hover:bg-[#F7F4ED] p-3 border-b border-slate-100 last:border-b-0 transition-colors"
                  >
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-teal-100 flex items-center justify-center mr-3 flex-shrink-0">
                        <PawPrint className="w-5 h-5 text-teal-600" />
                      </div>
                      <div className="flex-grow">
                        <div className="flex justify-between items-center">
                          <p className="text-sm font-semibold text-gray-800">{pet.name}</p>
                          <p className="text-xs text-gray-500">#{pet.protocolNo}</p>
                        </div>
                         <p className="text-xs text-gray-500 mb-1">
                           {pet.breed} • {pet.owner?.name} • {(() => {
                             const m = Number(pet.age || 0);
                             if (m < 12) return `${m} aylık`;
                             const y = Math.floor(m/12); const r = m % 12; 
                             return r === 0 ? `${y} yaşında` : `${y} yaş ${r} aylık`;
                           })()}
                         </p>
                        {pet.lastExamination && (
                          <div className="text-xs text-gray-600 mt-1 pt-1 border-t border-gray-200">
                            <p><strong>Son Muayene:</strong> {new Date(pet.lastExamination.date).toLocaleDateString('tr-TR')}</p>
                            {pet.lastExamination.diagnosis && (
                              <p className="truncate"><strong>Teşhis:</strong> {pet.lastExamination.diagnosis}</p>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Dark Mode Toggle */}
          <DarkModeToggle />

          {/* Kullanıcı Menüsü */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-[#F0EBE1] transition-colors"
            >
              <div className="hidden md:block text-right">
                <p className="text-[13px] font-semibold text-[#2B2B2B] leading-tight">{userName}</p>
                <p className="text-[11px] text-[#8A8676] leading-tight">
                  {clinicLine ? `${clinicLine}` : userRole}
                </p>
              </div>
              <div className="w-8 h-8 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-full flex items-center justify-center text-white text-[11px] font-bold flex-shrink-0 shadow-md shadow-teal-300/30">
                {userInitials}
              </div>
              <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform hidden md:block ${showUserMenu ? 'rotate-180' : ''}`} />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-52 rounded-xl shadow-xl py-1.5 z-50"
                style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}>
                <div className="px-3.5 py-2.5 border-b border-[#E8E2D4] mb-1">
                  <p className="text-[13px] font-bold text-[#2B2B2B] truncate">{userName}</p>
                  <p className="text-[11px] text-[#8A8676] truncate mt-0.5">{user.email}</p>
                </div>

                <Link to="/app/account" onClick={() => setShowUserMenu(false)}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[13px] text-[#2B2B2B] hover:bg-[#F7F4ED] transition-colors">
                  <Shield className="w-3.5 h-3.5 text-teal-600" />
                  Hesap Ayarları
                </Link>
                <Link to="/app/clinic-settings" onClick={() => setShowUserMenu(false)}
                  className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[13px] text-[#2B2B2B] hover:bg-[#F7F4ED] transition-colors">
                  <Building2 className="w-3.5 h-3.5 text-[#8A8676]" />
                  Klinik Ayarları
                </Link>

                <div className="border-t border-[#E8E2D4] mt-1 pt-1">
                  <button onClick={handleLogout}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 text-[13px] text-rose-600 hover:bg-rose-50 transition-colors">
                    <LogOut className="w-3.5 h-3.5" />
                    Çıkış Yap
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Overlay - Menü dışına tıklandığında kapat */}
      {showUserMenu && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowUserMenu(false)}
        />
      )}
      
      {/* Arama sonuçları dışına tıklandığında kapat */}
      {showSearchResults && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => setShowSearchResults(false)}
        />
      )}

      <PetDetailModal 
        isOpen={isPetDetailModalOpen}
        onClose={() => setIsPetDetailModalOpen(false)}
        pet={selectedPetForModal}
      />
    </header>
  );
};

export default Header;